import { Button, Input, Tabs } from '@artosera/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { usePresse, type ArticlePresente } from '../../api/presse.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction } from '../../auth/sujet.js';
import { Monogramme } from '../../composants/Monogramme.js';
import { useShellContext } from '../shell/context.js';
import { useDebounce } from '../oeuvres/useDebounce.js';
import { NouvelArticleDialog } from './NouvelArticleDialog.js';
import { LiensPresse } from './LiensPresse.js';
import styles from './PressePage.module.css';
import { Verrouillable } from '../module/LectureSeule.js';
export function PressePage() {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const [parametres] = useSearchParams();
  const rattachements = {
    ...(parametres.get('expositionId') ? { expositionId: parametres.get('expositionId')! } : {}),
    ...(parametres.get('artisteId') ? { artisteId: parametres.get('artisteId')! } : {}),
  };
  const [genre, setGenre] = useState('tout');
  const [q, setQ] = useState('');
  const recherche = useDebounce(q, 250);
  const [page, setPage] = useState(1);
  const [ajouter, setAjouter] = useState(false);
  const [archives, setArchives] = useState(false);
  const presse = usePresse(galerieSlug, {
    ...rattachements,
    ...(genre !== 'tout' ? { genre: genre as ArticlePresente['genre'] } : {}),
    q: recherche,
    archives,
    page,
    parPage: 30,
  });
  const liste = (
    <>
      <div className={styles.liste}>
        {presse.data?.articles.map((a) => (
          <article className={styles.carte} key={a.id} data-ui="press-card">
            <div className={styles.signature}>
              <Monogramme nom={a.support || a.auteur} forme="carre" />
              <span>{a.support}</span>
              {a.paruLe ? (
                <time dateTime={a.paruLe}>
                  {new Intl.DateTimeFormat(i18n.language, {
                    dateStyle: 'medium',
                    timeZone: 'Europe/Paris',
                  }).format(new Date(`${a.paruLe}T12:00:00Z`))}
                </time>
              ) : null}
            </div>
            <h2>
              <Link to={`/${galerieSlug}/presse/${a.id}`}>{a.titre}</Link>
            </h2>
            <p>{a.auteur}</p>
            <LiensPresse article={a} />
            <p className={styles.meta}>
              {t('regie:presse.nombreFichiers', { count: a.fichiers.length })}
            </p>
          </article>
        ))}
      </div>
      {presse.data && !presse.data.articles.length ? <p>{t('regie:presse.vide')}</p> : null}
      {presse.data && presse.data.pagination.total > 30 ? (
        <div className={styles.actions}>
          <Button variant="secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            {t('regie:presse.precedent')}
          </Button>
          <span>{page}</span>
          <Button
            variant="secondary"
            disabled={page * 30 >= presse.data.pagination.total}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('regie:presse.suivant')}
          </Button>
        </div>
      ) : null}
    </>
  );
  return (
    <div className={styles.page} data-ui="press-page">
      <header className={styles.entete} data-ui="page-header">
        <h1>{t('regie:presse.titre')}</h1>
        <Verrouillable action="press:write" ouvert={galerieALaction(galerie, 'press:write')}>
          <Button onClick={() => setAjouter(true)}>{t('regie:presse.ajouter')}</Button>
        </Verrouillable>
      </header>
      <div className={styles.actions} data-ui="filters">
        <Input
          aria-label={t('regie:presse.recherche')}
          placeholder={t('regie:presse.recherche')}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />
        <Button
          variant="secondary"
          aria-pressed={archives}
          onClick={() => {
            setArchives((v) => !v);
            setPage(1);
          }}
        >
          {t('regie:presse.archives')}
        </Button>
      </div>
      {presse.isLoading ? <p>{t('commun:chargement')}</p> : null}
      {presse.isError ? (
        <p role="alert">
          {estApiError(presse.error) ? presse.error.message : t('commun:erreurReseau')}
        </p>
      ) : null}
      <Tabs
        value={genre}
        onValueChange={(v) => {
          setGenre(v);
          setPage(1);
        }}
        tabs={['tout', 'article', 'catalogue'].map((v) => ({
          value: v,
          label: t(`regie:presse.onglets.${v}`),
          content: liste,
        }))}
      />
      <NouvelArticleDialog open={ajouter} onOpenChange={setAjouter} />
    </div>
  );
}
